import {
  OBJECTIVE_TYPE,
  REVIEW_STATUS,
  objectiveKey,
} from "../../_config/quarterlyOkrConfig";

// ─────────────────────────────────────────────────────────────────────────────
// The Meeting Agenda, built from the `qokr_lr_details` snapshot in the
// `SummaryGroups` shape both apps' review screens use (EXW / Strategy Canvas):
// `{ key, name, color, items: [{ id, kind, title, status, discussed,
// comments }] }`. `kind` / `color` are the panel's own string values.
//
// What makes the agenda: every to-discuss objective and every accepted
// objective the coach commented on (struck through as "Discussed" once
// settled, never dropped — rows don't jump mid-sentence), and the coach's comments about a
// person or the quarter as a whole. Company first, then each person, then the
// whole quarter.
// ─────────────────────────────────────────────────────────────────────────────

const KIND = { BLOCK: "block", SECTION: "section", OVERALL: "overall" };

const hasText = (comment) => Boolean(comment?.comment_text?.trim());

export const objectiveComments = (comments, type, objectiveId) =>
  comments.filter(
    (comment) =>
      comment.scope_type === "objective" &&
      String(comment[`${type}_objective_id`]) === String(objectiveId) &&
      hasText(comment),
  );

const agendaItem = (type, objective, comments) => {
  const notes = objectiveComments(comments, type, objective.objective_id);
  const toDiscuss = objective.review?.status === REVIEW_STATUS.TO_DISCUSS;
  if (!toDiscuss && !notes.length) return null;
  return {
    // The same key the board uses, so a click opens the row.
    id: objectiveKey(type, objective.objective_id),
    kind: KIND.BLOCK,
    title: objective.objective,
    status: objective.review?.status || REVIEW_STATUS.ACCEPTED,
    discussed: Boolean(objective.review?.discussed_at),
    comments: notes,
  };
};

// Every individual objective in the snapshot, wherever it sits.
export const allIndividuals = (details) => [
  ...(details?.company_objectives || []).flatMap(
    (company) => company.individual_objectives || [],
  ),
  ...(details?.not_linked || []),
];

export const buildLiveAgenda = (details) => {
  if (!details) return [];
  const comments = details.coach_comments || [];
  const groups = [
    {
      key: "company",
      name: "Company OKRs",
      color: "core",
      items: (details.company_objectives || [])
        .map((o) => agendaItem(OBJECTIVE_TYPE.COMPANY, o, comments))
        .filter(Boolean),
    },
  ];

  (details.members || []).forEach((member) => {
    const aboutThem = comments.filter(
      (c) =>
        c.scope_type === "member" &&
        String(c.member_user_id) === String(member.user_id) &&
        hasText(c),
    );
    const items = [
      ...(aboutThem.length
        ? [
            {
              id: `member:${member.user_id}`,
              kind: KIND.SECTION,
              // Not the Canvas "Section comment": it's about a person.
              badge: "Person comment",
              title: `About ${member.name}`,
              status: null,
              comments: aboutThem,
            },
          ]
        : []),
      ...allIndividuals(details)
        .filter((o) => String(o.member?.user_id) === String(member.user_id))
        .map((o) => agendaItem(OBJECTIVE_TYPE.INDIVIDUAL, o, comments))
        .filter(Boolean),
    ];
    groups.push({
      key: `member:${member.user_id}`,
      name: member.name,
      color: "dna",
      items,
    });
  });

  groups.push({
    key: "overall",
    name: "The quarter as a whole",
    color: "dark",
    items: comments
      .filter((c) => c.scope_type === "company" && hasText(c))
      .map((comment) => ({
        id: `overall:${comment.coach_comment_id}`,
        kind: KIND.OVERALL,
        title: "Overall",
        status: null,
        comments: [comment],
      })),
  });

  return groups.filter((group) => group.items.length);
};
